"use client";

import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { HeroTrialCta } from "@/components/landing/hero-trial-cta";
import { PRICE_FIRST_MARKET, priceFirstOrder } from "@/lib/marketing/youtube-price-first";
import { getSportsbookLogoPath } from "@/lib/sportsbook-logo";
import { phCapture } from "@/utils/analytics";
import styles from "./home-conversion.module.css";

const STAKE = 10;

function priceFirstBook(id: (typeof PRICE_FIRST_MARKET.books)[number]["id"]) {
  const book = PRICE_FIRST_MARKET.books.find((candidate) => candidate.id === id);
  if (!book) throw new Error(`Missing price-first book: ${id}`);
  return book;
}

const fanduel = priceFirstBook("fanduel");
const novig = priceFirstBook("novig");

export function HomeQuickDemo() {
  const [odds, setOdds] = useState<number>(novig.odds);
  const [finished, setFinished] = useState(false);
  const order = priceFirstOrder(1000, STAKE, odds);
  const fanduelOrder = priceFirstOrder(1000, STAKE, fanduel.odds);
  const better = odds === novig.odds;
  const select = (value: number) => {
    setOdds(value);
    setFinished(false);
    phCapture("homepage_demo_price_selected", { surface: "home_quick_demo", example_id: PRICE_FIRST_MARKET.id, odds: value });
  };

  return <section id="how-it-works" className={`${styles.section} ${styles.demoSection}`} aria-labelledby="home-demo-title">
    <div className={styles.demoLayout}>
      <div>
        <h2 id="home-demo-title" className={styles.title}>Same bet.<br /><span>A better price.</span></h2>
        <ol className={styles.steps}>
          <li><span>1</span><div><b>Find the opportunity</b></div></li>
          <li data-active={!finished}><span>2</span><div><b>Compare the price</b></div></li>
          <li data-active={finished}><span>3</span><div><b>Decide, then place it</b></div></li>
        </ol>
        <a href="#pricing" className={styles.textLink}>Ready already? Compare plans <ArrowRight size={16} /></a>
      </div>
      <div className={styles.demoCard}>
        <h3>{PRICE_FIRST_MARKET.game}</h3><p className={styles.market}>{PRICE_FIRST_MARKET.outcome}</p>
        <div className={styles.bookChoices}>
          {[fanduel, novig].map((book) => <button key={book.id} type="button" aria-pressed={book.odds === odds} onClick={() => select(book.odds)}>
            <span><img src={getSportsbookLogoPath(book.id)} alt="" width={28} height={28} />{book.name}</span><strong className="data-num">+{book.odds}</strong><small>{book.odds === odds ? <><Check size={13} /> Selected</> : "Compare this price"}</small>
          </button>)}
        </div>
        <div className={styles.demoResult} aria-live="polite">
          {order && fanduelOrder && <><div className={styles.returnRow}><span>{`Profit if your $${STAKE} bet wins`}<strong className="data-num">${order.profit.toFixed(2)}</strong></span><span>Estimated price edge<strong className="data-num" data-positive={order.ev > 0}>{order.ev > 0 ? "+" : ""}{order.ev.toFixed(1)}%</strong></span></div><p>{better ? `$${(order.profit - fanduelOrder.profit).toFixed(2)} more profit on the same $${STAKE} winning bet. That better price turns this example’s estimated edge positive.` : `At ${fanduel.name} +${fanduel.odds}, the same prediction pays less. Try ${novig.name} +${novig.odds} to see the extra return and the change in estimated edge.`}</p></>}
        </div>
        {finished ? <div className={styles.nextStep} aria-live="polite"><b><Check size={18} /> You’ve compared the market.</b><p>In Upside, open the listed platform from the opportunity. Confirm the line and stake there. Upside finds the prices; you decide whether to bet.</p></div> : <button type="button" className={styles.demoNext} onClick={() => { setFinished(true); phCapture("homepage_demo_completed", { surface: "home_quick_demo", example_id: PRICE_FIRST_MARKET.id, odds }); }}>See what to do next <ArrowRight size={16} /></button>}
        <p className={styles.note}>Illustrative 42.3% win estimate from the video example. Edge is an estimate before costs; any individual bet can lose. No bet is placed here.</p>
      </div>
    </div>
    <div className={styles.afterDemo}><HeroTrialCta placement="demo" /></div>
  </section>;
}
